/* La Pepa · In den Warenkorb (DESIGN.md, Baustein „In den Warenkorb“)
 * Ein Ablauf für alle Kaufbuttons: Spinner, „Listo“ mit Haken, Mengen-Badge, Bild fliegt zum Warenkorb-Icon,
 * Icon wippt, Konfetti; Fehler: Button schüttelt. Der Drawer öffnet sich nicht, er bekommt nur neuen Inhalt:
 * theme.js öffnet ihn allein bei cart:change mit baseEvent 'variant:add' (CartDrawer, onCartChange).
 *
 * - Fängt jedes <form is="product-form"> außerhalb des Drawers ab (Produktseite, Sticky-Leiste über form=,
 *   Quick-Add auf Karten, Quick-Buy-Modal) und schickt es selbst ab; FormData bleibt unverändert
 *   (Eigenschaften wie _origen, Selling-Plans). Danach variant:add am Formular (Modal schließt, Apps) und
 *   cart:change mit baseEvent 'lp:add'. Gilt auch auf der Warenkorbseite: sections/lp-main-cart rendert sich dort selbst neu.
 * - window.LpCart für eigene Bausteine (Complementa, Sabores): add(), fly(), bump(), confetti(), decorate(), money().
 */
const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const strings = () => window.lpStrings || {};
const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
const confettiColors = () => ['--lp-brand', '--lp-lime', '--lp-pastel-pink', '--lp-plum'].map(cssVar).filter(Boolean);

let lastCart = null;
const forms = new Set();

/* ---------- Button ---------- */
const CHECK = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';

// Legt Label, Spinner, „Listo“ und Mengen-Badge in den Button. Prestige ersetzt beim Variantenwechsel den
// Button-Inhalt (BuyButtons, innerHTML); der Observer baut die Teile dann neu auf.
function decorate(btn) {
  if (!btn) return;
  if (!btn.querySelector(':scope > .lp-buy__label')) {
    const label = document.createElement('span');
    label.className = 'lp-buy__label';
    label.append(...btn.childNodes);
    const ok = document.createElement('span');
    ok.className = 'lp-buy__ok';
    ok.innerHTML = `${CHECK}<span>${strings().added || 'Listo'}</span>`;
    const spin = Object.assign(document.createElement('span'), { className: 'lp-buy__spin' });
    const qty = Object.assign(document.createElement('span'), { className: 'lp-buy__qty' });
    spin.setAttribute('aria-hidden', 'true');
    qty.setAttribute('aria-hidden', 'true');
    btn.append(label, spin, ok, qty);
  }
  btn.classList.add('lp-buy');
  if (!btn.lpObserved) {
    btn.lpObserved = true;
    new MutationObserver(() => {
      if (!btn.querySelector(':scope > .lp-buy__label')) {
        decorate(btn);
        const form = btn.form;
        if (form) syncForm(form);
      }
    }).observe(btn, { childList: true });
  }
}

function setQty(btn, q) {
  const el = btn && btn.querySelector(':scope > .lp-buy__qty');
  if (!el) return;
  el.textContent = q > 0 ? q : '';
  el.classList.toggle('is-on', q > 0);
}

/* ---------- Effekte ---------- */
function cartLink() {
  return document.querySelector('.lp-header__actions a[aria-controls="cart-drawer"]')
    || document.querySelector('a[aria-controls="cart-drawer"]')
    || document.querySelector('header a[href$="/cart"]');
}

function flyTarget() {
  const link = cartLink();
  const icon = link && (link.querySelector('svg') || link);
  if (icon) {
    const r = icon.getBoundingClientRect();
    if (r.width > 0 && r.bottom > 0 && r.top < innerHeight) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  return { x: innerWidth - 28, y: 28 };
}

const inView = (el) => {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.bottom > 60 && r.top < innerHeight;
};

// Bild fliegt von `from` (Bild oder Button) zum Warenkorb-Icon, als Quadrat bis 160 px.
// natural: Originalmaße ohne Zuschnitt (freigestellte PNGs wie die Sabores-Tuben).
function fly(from, src, { natural = false } = {}) {
  if (!from || !src || reduceMotion() || !document.body.animate) return Promise.resolve();
  const r = from.getBoundingClientRect();
  const size = Math.min(r.width, r.height, 160) || 64;
  const w = natural ? r.width : size, h = natural ? r.height : size;
  const t = flyTarget();
  const el = new Image();
  el.src = src;
  el.alt = '';
  el.className = natural ? 'lp-fly lp-fly--natural' : 'lp-fly';
  Object.assign(el.style, {
    left: `${r.left + r.width / 2 - w / 2}px`, top: `${r.top + r.height / 2 - h / 2}px`,
    width: `${w}px`, height: `${h}px`,
  });
  document.body.appendChild(el);
  const dx = t.x - (r.left + r.width / 2), dy = t.y - (r.top + r.height / 2);
  const anim = el.animate([
    { transform: 'translate(0, 0) scale(1) rotate(0deg)' },
    { transform: `translate(${dx * 0.3}px, ${dy * 0.3 - 90}px) scale(.62) rotate(-18deg)`, offset: 0.42 },
    { transform: `translate(${dx}px, ${dy}px) scale(.08) rotate(24deg)`, opacity: 0.7 },
  ], { duration: 820, easing: 'cubic-bezier(.55, 0, .35, 1)' });
  return new Promise((resolve) => {
    const done = () => { el.remove(); resolve(); };
    anim.onfinish = done;
    anim.oncancel = done;
    setTimeout(done, 1200);
  });
}

// Mehrere Bilder leicht versetzt; unsichtbare Bilder starten am Button.
function flyAll(images, button) {
  const list = (images || []).filter(Boolean);
  return Promise.all(list.map((img, k) => new Promise((resolve) => {
    setTimeout(() => fly(inView(img) ? img : (button || img), img.currentSrc || img.src).then(resolve), k * 120);
  })));
}

function bump() {
  const link = cartLink();
  if (!link || reduceMotion()) return;
  link.classList.remove('lp-cart-bump');
  void link.offsetWidth;
  link.classList.add('lp-cart-bump');
  link.addEventListener('animationend', () => link.classList.remove('lp-cart-bump'), { once: true });
}

function confetti(el, colors) {
  if (!el || reduceMotion() || !document.body.animate) return;
  const palette = colors && colors.length ? colors : confettiColors();
  const r = el.getBoundingClientRect(), x = r.left + r.width / 2, y = r.top + r.height / 2;
  for (let i = 0; i < 16; i++) {
    const s = document.createElement('i');
    s.className = 'lp-confetti';
    Object.assign(s.style, { left: `${x}px`, top: `${y}px`, background: palette[i % palette.length] });
    if (i % 3 === 0) s.style.borderRadius = '50%';
    document.body.appendChild(s);
    const ang = Math.random() * Math.PI * 2, dist = 40 + Math.random() * 50;
    s.animate([
      { transform: 'translate(-50%, -50%) scale(1) rotate(0)', opacity: 1 },
      { transform: `translate(${Math.cos(ang) * dist}px, ${Math.sin(ang) * dist - 20}px) scale(.4) rotate(${Math.random() * 360}deg)`, opacity: 0 },
    ], { duration: 650 + Math.random() * 250, easing: 'cubic-bezier(.2, .8, .3, 1)' }).onfinish = () => s.remove();
  }
}

const money = (cents) => `$${Math.round(cents / 100).toLocaleString('es-CO')}`;

/* ---------- Warenkorb ---------- */
async function request(body) {
  const sections = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
  let init;
  if (body instanceof FormData) {
    body.set('sections', sections.join(','));
    body.set('sections_url', location.pathname);
    init = { method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest' } };
  } else {
    init = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: body, sections: sections.join(','), sections_url: location.pathname }),
    };
  }
  const res = await fetch(`${root()}cart/add.js`, init);
  const json = await res.json();
  if (!res.ok) {
    const err = new Error(json.description || json.message || strings().error || '');
    err.status = res.status;
    throw err;
  }
  const cart = await (await fetch(`${root()}cart.js`)).json();
  cart.sections = json.sections;
  return { cart, items: json.items || [json] };
}

// body: FormData oder [{ id, quantity, properties }]. button bekommt die Rückmeldung, images fliegen.
async function add({ body, button, images, form } = {}) {
  if (button) {
    if (button.classList.contains('is-loading')) return null;
    decorate(button);
    button.classList.remove('is-done', 'is-error');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
  }
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
  try {
    const { cart, items } = await request(body);
    if (button) {
      button.classList.replace('is-loading', 'is-done');
      confetti(button);
    }
    await flyAll(images, button);
    if (form) form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart, onSuccessDo: null } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'lp:add', cart } }));
    bump();
    if (button) {
      clearTimeout(button.lpDoneTimer);
      button.lpDoneTimer = setTimeout(() => button.classList.remove('is-done'), 1600);
    }
    return cart;
  } catch (err) {
    if (button) {
      button.classList.remove('is-loading');
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 500);
    }
    if (form) form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: err.message } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    throw err;
  } finally {
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    if (button) button.removeAttribute('aria-busy');
  }
}

/* ---------- Produktformulare ---------- */
const submitButtons = (form) => [...form.elements].filter((el) => el.type === 'submit');

function variantQty(form) {
  if (!lastCart) return 0;
  const id = Number(new FormData(form).get('id'));
  return lastCart.items.reduce((n, it) => n + (it.variant_id === id ? it.quantity : 0), 0);
}

function syncForm(form) {
  const q = variantQty(form);
  submitButtons(form).forEach((btn) => setQty(btn, q));
}

function register(form) {
  if (forms.has(form)) return;
  forms.add(form);
  submitButtons(form).forEach(decorate);
  form.addEventListener('variant:change', () => requestAnimationFrame(() => syncForm(form)));
}

// Bild zum Formular: Karte, Quick-Buy-Modal oder Galerie der Produktseite (das sichtbarste Bild).
function formImages(form, button) {
  const scope = button.closest('.product-card') || form.closest('.product-card')
    || form.closest('quick-buy-modal') || form.closest('.shopify-section') || document;
  const imgs = [...scope.querySelectorAll('.product-gallery img, .product-card__figure img, .quick-buy-modal img, img')]
    .filter((img) => !img.closest('.product-sticky-bar, #cart-drawer') && (img.currentSrc || img.src));
  const visible = imgs.filter(inView).sort((a, b) => b.getBoundingClientRect().width - a.getBoundingClientRect().width);
  return visible.length ? [visible[0]] : imgs.slice(0, 1);
}

const intercept = (form) => form.matches('form[is="product-form"]')
  && !form.closest('#cart-drawer')
  && !(window.themeVariables && themeVariables.settings && themeVariables.settings.cartType === 'page');

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !intercept(form)) return;
  event.preventDefault();
  event.stopImmediatePropagation(); // ProductForm (theme.js) würde sonst selbst senden und den Drawer öffnen
  if (!form.checkValidity()) { form.reportValidity(); return; }
  register(form);
  const button = event.submitter || submitButtons(form)[0];
  if (!button || button.disabled) return;
  add({ body: new FormData(form), button, images: formImages(form, button), form }).catch(() => {});
}, true);

document.addEventListener('cart:change', (event) => {
  const cart = event.detail && event.detail.cart;
  if (!cart || !Array.isArray(cart.items)) return;
  lastCart = cart;
  forms.forEach((form) => (form.isConnected ? syncForm(form) : forms.delete(form)));
});

// Produktseite: Formulare sofort vorbereiten, Menge aus dem aktuellen Warenkorb.
const pageForms = [...document.querySelectorAll('form[is="product-form"]')]
  .filter((form) => intercept(form) && !form.closest('.product-card, quick-buy-modal'));
if (pageForms.length) {
  pageForms.forEach(register);
  fetch(`${root()}cart.js`).then((r) => r.json()).then((cart) => {
    lastCart = cart;
    pageForms.forEach(syncForm);
  }).catch(() => {});
}

// Preise beim Variantenwechsel: theme.js schreibt sie im Shop-Format („,00“) und kennt das Rabatt-Badge
// (price-list, data-lp-price-off) nicht. Läuft am document nach den Listenern am Formular, auch im Quick-Buy-Modal.
document.addEventListener('variant:change', (event) => {
  const formId = event.target.getAttribute && event.target.getAttribute('id');
  const variant = event.detail && event.detail.variant;
  // „Comprar por WhatsApp“ nur bei kaufbarer Variante (Kombination ohne Variante zählt als nicht kaufbar)
  const section = formId && event.target.closest('.shopify-section');
  if (section) section.querySelectorAll('.lp-wa-buy').forEach((el) => { el.hidden = !(variant && variant.available); });
  if (!formId || !variant) return;
  const sel = (tag) => document.querySelectorAll(`${tag}[form="${CSS.escape(formId)}"]`);
  sel('sale-price').forEach((el) => { if (el.lastChild) el.lastChild.textContent = money(variant.price); });
  sel('compare-at-price').forEach((el) => { if (el.lastChild && variant.compare_at_price) el.lastChild.textContent = money(variant.compare_at_price); });
  const off = variant.compare_at_price > variant.price ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
  document.querySelectorAll(`[data-lp-price-off="${CSS.escape(formId)}"]`).forEach((el) => {
    el.textContent = `-${off}%`;
    el.hidden = off < 1;
  });
});

window.LpCart = { add, request, fly, flyAll, bump, confetti, decorate, setQty, cartLink, money };
